// File path: /shell/src/context/ModuleContext.tsx
// Module Context - Manages active module state and lazy loading

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ModuleId } from '@core/config/moduleRegistry';
import type {
  ModuleEntryPoint,
  LoadedModule,
  ModuleLoadingState,
  ModuleContextValue,
} from '@core/types/module';

// Module loader registry - maps module IDs to their dynamic imports
const moduleLoaders: Record<string, () => Promise<{ default: ModuleEntryPoint }>> = {
  elevate: () => import('@modules/elevate/module'),
  navigate: () => import('@modules/navigate/module'),
  bridge: () => import('@modules/bridge/module'),
  // Future modules:
  // pathway: () => import('@modules/pathway/module'),
};

// Whether a module has a registered loader — the one source of truth for "can this module be opened"
// (the tab bar shows a tab only for such a module, C-PHTABS)
export const hasModuleLoader = (moduleId: string): boolean => moduleId in moduleLoaders;

// Create the context
const ModuleContext = createContext<ModuleContextValue | undefined>(undefined);

// Hook to use the module context
export const useModule = (): ModuleContextValue => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModule must be used within a ModuleProvider');
  }
  return context;
};

// Provider component
export const ModuleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeModule, setActiveModule] = useState<ModuleId | null>(null);
  const [loadedModules, setLoadedModules] = useState<Map<ModuleId, LoadedModule>>(new Map());

  // Load a module by ID
  const loadModule = useCallback(async (moduleId: ModuleId): Promise<ModuleEntryPoint> => {
    // Check if already loaded
    const existingModule = loadedModules.get(moduleId);
    if (existingModule?.loadingState === 'loaded' && existingModule.entryPoint) {
      console.log(`Module ${moduleId} already loaded`);
      return existingModule.entryPoint;
    }

    // Check if currently loading
    if (existingModule?.loadingState === 'loading') {
      console.log(`Module ${moduleId} is currently loading, waiting...`);
      // Wait for the module to finish loading
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const module = loadedModules.get(moduleId);
          if (module?.loadingState === 'loaded' && module.entryPoint) {
            clearInterval(checkInterval);
            resolve(module.entryPoint);
          } else if (module?.loadingState === 'error') {
            clearInterval(checkInterval);
            reject(module.error);
          }
        }, 100);
      });
    }

    // Check if we have a loader for this module
    const loader = moduleLoaders[moduleId];
    if (!loader) {
      const error = new Error(`No loader found for module: ${moduleId}`);
      console.error(error);
      throw error;
    }

    // Update state to loading
    setLoadedModules(prev => {
      const updated = new Map(prev);
      updated.set(moduleId, {
        moduleId,
        entryPoint: null as any, // Will be set when loaded
        loadedAt: new Date(),
        loadingState: 'loading' as ModuleLoadingState,
      });
      return updated;
    });

    try {
      console.log(`Loading module: ${moduleId}`);
      const moduleExport = await loader();
      const entryPoint = moduleExport.default;

      // Update state to loaded
      setLoadedModules(prev => {
        const updated = new Map(prev);
        updated.set(moduleId, {
          moduleId,
          entryPoint,
          loadedAt: new Date(),
          loadingState: 'loaded' as ModuleLoadingState,
        });
        return updated;
      });

      console.log(`Module ${moduleId} loaded successfully`);
      return entryPoint;
    } catch (error) {
      console.error(`Failed to load module ${moduleId}:`, error);

      // Update state to error
      setLoadedModules(prev => {
        const updated = new Map(prev);
        updated.set(moduleId, {
          moduleId,
          entryPoint: null as any,
          loadedAt: new Date(),
          loadingState: 'error' as ModuleLoadingState,
          error: error as Error,
        });
        return updated;
      });

      throw error;
    }
  }, [loadedModules]);

  // Check if a module is loaded
  const isModuleLoaded = useCallback((moduleId: ModuleId): boolean => {
    const module = loadedModules.get(moduleId);
    return module?.loadingState === 'loaded';
  }, [loadedModules]);

  // Get a loaded module's entry point
  const getModuleEntryPoint = useCallback((moduleId: ModuleId): ModuleEntryPoint | undefined => {
    const module = loadedModules.get(moduleId);
    if (module?.loadingState === 'loaded') {
      return module.entryPoint;
    }
    return undefined;
  }, [loadedModules]);

  // Memoize the context value
  const contextValue = useMemo<ModuleContextValue>(() => ({
    activeModule,
    setActiveModule,
    loadedModules,
    loadModule,
    isModuleLoaded,
    getModuleEntryPoint,
  }), [activeModule, loadedModules, loadModule, isModuleLoaded, getModuleEntryPoint]);

  return (
    <ModuleContext.Provider value={contextValue}>
      {children}
    </ModuleContext.Provider>
  );
};

export default ModuleContext;
